import { useEffect, useState } from "react";
import { RefreshCcw, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const Item = ({ it }) => (
  <a href={it.url} target="_blank" rel="noopener noreferrer" data-testid={`feed-item-${it.id}`}
     className="rounded-2xl bg-white border border-stone-200 hover:border-stone-300 hover:shadow-md transition-shadow overflow-hidden flex flex-col">
    <div className="aspect-[3/4] bg-stone-100">
      {it.image && <img src={it.image} alt="" loading="lazy" className="w-full h-full object-cover" />}
    </div>
    <div className="p-2 min-w-0">
      <div className="text-xs font-black text-stone-900 line-clamp-2">{it.title}</div>
      <div className="text-[10px] text-stone-500 line-clamp-1">
        {[it.airs, it.network, ...(it.genres || []).slice(0, 2), it.platform, (it.authors || [])[0], it.year].filter(Boolean).join(" · ")}
      </div>
    </div>
  </a>
);

// One real-data shelf (TVMaze / FreeToGame / Open Library) with provider credit + freshness; never placeholders.
export const FeedShelf = ({ feed, title, q = "", country = "global", limit }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (refresh = false) => {
    setBusy(true);
    api.get(`/entertainment/feeds/${feed}`, { params: { q, country, ...(refresh ? { refresh: 1 } : {}) } })
      .then((r) => setD(r.data))
      .catch(() => setD({ status: "unavailable", items: [], reason: "Could not reach find2near." }))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [feed, q, country]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = (d?.items || []).slice(0, limit || 24);
  return (
    <section className="mb-6" data-testid={`feed-${feed}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="text-[13px] uppercase tracking-widest font-black text-stone-700">{title}{q && <span className="normal-case tracking-normal font-bold text-stone-500"> · “{q}”</span>}</div>
        <button onClick={() => load(true)} disabled={busy} className="text-[11px] font-bold text-stone-500 hover:text-stone-900 inline-flex items-center gap-1" data-testid={`feed-refresh-${feed}`}>
          {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCcw size={11} />} Refresh
        </button>
      </div>
      {!d ? <div className="py-8 text-center text-stone-400"><Loader2 className="mx-auto animate-spin" /></div>
        : d.status !== "ok" ? <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-4 text-sm text-stone-600" data-testid={`feed-unavailable-${feed}`}>Unavailable right now. {d.reason}</div>
        : !items.length ? <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-4 text-sm text-stone-600" data-testid={`feed-empty-${feed}`}>No results{q ? ` for “${q}”` : ""}.</div>
        : <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(118px, 1fr))" }}>{items.map((it) => <Item key={it.id} it={it} />)}</div>}
      {d?.provider && (
        <div className="text-[10px] text-stone-400 mt-1" data-testid={`feed-credit-${feed}`}>
          {d.provider.attribution} — <a href={d.provider.url} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">{d.provider.name}<ExternalLink size={8} /></a>
          {" "}({d.provider.license}) · updated {d.fetched_at ? new Date(d.fetched_at).toLocaleString() : "—"}{d.cached ? " (cached)" : ""}
        </div>
      )}
    </section>
  );
};
